import React, { useCallback } from "react";

import { Box, Paper, Stack } from "@mui/material";

import { useModals } from "../../../hooks";
import {
  useMeasureParametersSelector,
  useTaggerActions,
  useTaggerDocumentSelector,
  useTaggerDocumentTagsSelector
} from "../../../store";
import { AltiusErrors, TaggerDocument, TagPhrasePayload } from "../../../store/types";
import DocumentTopBar from "../DocumentTopBar/DocumentTopBar";
import EmptyDocumentView from "./EmptyDocumentView";
import PDFViewer from "./PDFViewer/PDFViewer";

interface Props {
  file: File | null;
  docId: TaggerDocument["id"];
  setTableOpen: () => void;
  onLoadFiles?: (files: File[]) => void;
  disabled?: boolean;
  currentDocIndex: number;
  setCurrentDocIndex: (index: number) => void;
}

export const DocumentView: React.FC<Props> = ({
  docId,
  file,
  onLoadFiles,
  setTableOpen,
  disabled = false,
  currentDocIndex,
  setCurrentDocIndex
}) => {
  const document = useTaggerDocumentSelector(docId);
  const tags = useTaggerDocumentTagsSelector(docId);

  const measures = useMeasureParametersSelector();
  const { tagPhrase } = useTaggerActions();
  const { pdfTaggerErrorModal } = useModals();

  const handleCreateTag = useCallback(
    async (data: Omit<TagPhrasePayload, "deal_id" | "document_id" | "is_measure_global_value">) => {
      const measureGlobalValue = measures.find((msr) => msr.id === data.measure_id)?.is_global_value || false;

      try {
        await tagPhrase({
          deal_id: document.dealId,
          document_id: docId,
          ...data,
          is_measure_global_value: measureGlobalValue
        });
      } catch (e) {
        const error = e as AltiusErrors;
        // If error has validation errors, throw it so TagMenu can handle them
        if (error?.errors && Object.keys(error.errors).length > 0) {
          throw e;
        }
        // Otherwise, show the modal for non-validation errors
        pdfTaggerErrorModal(error.errors!);
      }
    },
    [measures, document, docId, tagPhrase, pdfTaggerErrorModal]
  );

  return (
    <Stack sx={{ flexGrow: "1", width: "100%", marginBottom: "20px" }} gap="20px">
      <DocumentTopBar docId={docId} currentDocIndex={currentDocIndex} setCurrentDocIndex={setCurrentDocIndex} />
      <Paper
        sx={{
          display: "flex",
          // flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flexGrow: "1",
          width: "100%",
          padding: "15px 40px 40px 40px",
          position: "relative",
          ...(file && {
            padding: "67px 40px 40px 40px"
          })
        }}
      >
        {disabled && (
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              bgcolor: "rgba(255, 255, 255, 0.5)",
              zIndex: 100
            }}
          />
        )}
        {!file && onLoadFiles && <EmptyDocumentView onLoadFiles={onLoadFiles} />}
        {file && (
          <PDFViewer
            docId={docId}
            file={file}
            error={null}
            polygons={document?.polygons || null}
            tags={tags}
            onCreateTag={handleCreateTag}
            setTableOpen={setTableOpen}
            documentFileData={document.fileData}
          />
        )}
      </Paper>
    </Stack>
  );
};
